import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import useHttp from "../../hooks/use-http";
import { endPoints } from "../../config/end-points";
import {
  FiX,
  FiUpload,
  FiImage,
  FiSearch,
  FiCheck,
  FiTrash2,
  FiLoader,
  FiCamera,
  FiZap
} from "react-icons/fi";
import { cuteToast } from "../UI/cute";

const ImageUploadModal = ({ show, onClose, onImageSelect, currentImage, suggestedKeywords = [], aiImagePrompt = "" }) => {
  const { sendRequest } = useHttp();
  const fileInputRef = useRef(null);
  const token = useSelector((state) => state.auth.token);

  const [activeTab, setActiveTab] = useState("gallery"); // gallery, upload, unsplash, pexels
  const [isLoading, setIsLoading] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [savingExternal, setSavingExternal] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiGeneratedImage, setAiGeneratedImage] = useState(null);
  const [aiSize, setAiSize] = useState("1024x1024");
  const [aiQuality, setAiQuality] = useState("medium");

  useEffect(() => {
    if (show) {
      loadGallery();
      // Pre-fill AI prompt if provided
      if (aiImagePrompt) {
        setAiPrompt(aiImagePrompt);
      }
    }
  }, [show]);

  const loadGallery = async () => {
    setIsLoading(true);
    sendRequest(
      { url: endPoints.mediaGallery, method: "GET" },
      (response) => {
        setIsLoading(false);
        if (response.status === 200) {
          setGalleryImages(response.images || []);
        }
      }
    );
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      cuteToast({
        type: "error",
        title: "Invalid File",
        message: "Please upload a JPEG, PNG, GIF or WebP image",
        timer: 3000
      });
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      cuteToast({
        type: "error",
        title: "File Too Large",
        message: "Maximum file size is 10MB",
        timer: 3000
      });
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await fetch(endPoints.mediaUpload, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (data.status === 200) {
        cuteToast({
          type: "success",
          title: "Uploaded",
          message: "Image uploaded successfully",
          timer: 2000
        });
        // Select the uploaded image
        setSelectedImage(data.image);
        // Refresh gallery
        loadGallery();
        setActiveTab("gallery");
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      cuteToast({
        type: "error",
        title: "Upload Failed",
        message: error.message || "Failed to upload image",
        timer: 3000
      });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleSearch = async (source) => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setSearchResults([]);

    const endpoint = source === "unsplash" ? endPoints.mediaUnsplash : endPoints.mediaPexels;

    sendRequest(
      {
        url: `${endpoint}?query=${encodeURIComponent(searchQuery)}&per_page=24`,
        method: "GET"
      },
      (response) => {
        setIsLoading(false);
        if (response.status === 200) {
          setSearchResults(response.images || []);
        } else {
          cuteToast({
            type: "error",
            title: "Search Failed",
            message: response.message || "Failed to search images",
            timer: 3000
          });
        }
      }
    );
  };

  const handleAIGenerate = async () => {
    if (!aiPrompt.trim()) return;

    setAiGenerating(true);
    setAiGeneratedImage(null);

    try {
      const response = await fetch(endPoints.mediaGenerateAI, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt: aiPrompt.trim(),
          size: aiSize,
          quality: aiQuality
        })
      });

      const data = await response.json();

      if (data.status === 200 && data.image) {
        setAiGeneratedImage(data.image);
        setSelectedImage(data.image);
        cuteToast({ type: "success", title: "Image generated", message: "", timer: 2000 });
        // Refresh gallery
        loadGallery();
      } else {
        console.error("AI Image Generation Error - Full Response:", JSON.stringify(data, null, 2));
        throw new Error(data.message || "Generation failed");
      }
    } catch (error) {
      console.error("AI Image Generation Error:", error);
      cuteToast({
        type: "error",
        title: "Generation Failed",
        message: error.message || "Failed to generate image",
        timer: 5000
      });
    } finally {
      setAiGenerating(false);
    }
  };

  const handleSelectExternalImage = (image) => {
    // Just select the image, don't upload yet
    // Upload happens when user clicks "Use This Image"
    setSelectedImage(image);
  };

  const handleDeleteImage = async (image, e) => {
    e.stopPropagation();

    if (!window.confirm("Delete this image?")) return;

    sendRequest(
      {
        url: endPoints.mediaDelete,
        method: "POST",
        body: { key: image.key }
      },
      (response) => {
        if (response.status === 200) {
          cuteToast({
            type: "success",
            title: "Deleted",
            message: "Image deleted",
            timer: 2000
          });
          loadGallery();
          if (selectedImage?.key === image.key) {
            setSelectedImage(null);
          }
        }
      }
    );
  };

  const handleConfirmSelection = async () => {
    if (!selectedImage) return;

    // Check if this is an external image (from Unsplash/Pexels)
    if (selectedImage.source && (selectedImage.source === 'unsplash' || selectedImage.source === 'pexels')) {
      // Need to upload to S3 first
      setSavingExternal(true);

      sendRequest(
        {
          url: endPoints.mediaSaveExternal,
          method: "POST",
          body: { url: selectedImage.download_url, source: selectedImage.source }
        },
        (response) => {
          setSavingExternal(false);
          if (response.status === 200) {
            cuteToast({
              type: "success",
              title: "Saved",
              message: "Image saved to your gallery",
              timer: 2000
            });
            // Use the S3 URL
            onImageSelect(response.image.url);
            // Refresh gallery for next time
            loadGallery();
            // Switch to gallery tab and close
            setActiveTab("gallery");
            onClose();
          } else {
            cuteToast({
              type: "error",
              title: "Save Failed",
              message: response.message || "Failed to save image",
              timer: 3000
            });
          }
        }
      );
    } else {
      // Gallery image - already has S3 URL
      onImageSelect(selectedImage.url);
      onClose();
    }
  };

  if (!show) return null;

  return (
    <div className="image-modal-overlay" onClick={onClose}>
      <div className="image-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="image-modal-header">
          <h3>Select Image</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <FiX size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="image-modal-tabs">
          <button
            className={`tab-btn ${activeTab === "gallery" ? "active" : ""}`}
            onClick={() => setActiveTab("gallery")}
          >
            <FiImage size={16} /> Gallery
          </button>
          <button
            className={`tab-btn ${activeTab === "upload" ? "active" : ""}`}
            onClick={() => setActiveTab("upload")}
          >
            <FiUpload size={16} /> Upload
          </button>
          <button
            className={`tab-btn ${activeTab === "unsplash" ? "active" : ""}`}
            onClick={() => { setActiveTab("unsplash"); setSearchResults([]); }}
          >
            <FiCamera size={16} /> Unsplash
          </button>
          <button
            className={`tab-btn ${activeTab === "pexels" ? "active" : ""}`}
            onClick={() => { setActiveTab("pexels"); setSearchResults([]); }}
          >
            <FiCamera size={16} /> Pexels
          </button>
          <button
            className={`tab-btn ${activeTab === "ai" ? "active" : ""}`}
            onClick={() => setActiveTab("ai")}
            style={activeTab === "ai" ? {} : { background: 'rgba(124, 58, 237, 0.1)', color: '#a78bfa' }}
          >
            <FiZap size={16} /> AI Generate
          </button>
        </div>

        {/* Content */}
        <div className="image-modal-content">
          {/* Gallery Tab */}
          {activeTab === "gallery" && (
            <div className="gallery-grid">
              {isLoading ? (
                <div className="loading-state">
                  <FiLoader className="spin" size={32} />
                  <p>Loading gallery...</p>
                </div>
              ) : galleryImages.length === 0 ? (
                <div className="empty-state">
                  <FiImage size={48} />
                  <p>No images in your gallery</p>
                  <button className="btn-upload" onClick={() => setActiveTab("upload")}>
                    Upload your first image
                  </button>
                </div>
              ) : (
                galleryImages.map((image) => (
                  <div
                    key={image.key}
                    className={`gallery-item ${selectedImage?.url === image.url ? "selected" : ""}`}
                    onClick={() => setSelectedImage(image)}
                  >
                    <img src={image.url} alt={image.filename} />
                    {selectedImage?.url === image.url && (
                      <div className="selected-badge">
                        <FiCheck size={20} />
                      </div>
                    )}
                    <button
                      className="delete-btn"
                      onClick={(e) => handleDeleteImage(image, e)}
                    >
                      <FiTrash2 size={14} />
                    </button>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === "upload" && (
            <div className="upload-area">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept="image/jpeg,image/png,image/gif,image/webp"
                style={{ display: "none" }}
              />
              <div
                className="upload-dropzone"
                onClick={() => fileInputRef.current?.click()}
              >
                {isLoading ? (
                  <>
                    <FiLoader className="spin" size={48} />
                    <p>Uploading...</p>
                    {uploadProgress > 0 && <p>{uploadProgress}%</p>}
                  </>
                ) : (
                  <>
                    <FiUpload size={48} />
                    <p>Click to upload or drag and drop</p>
                    <small>JPEG, PNG, GIF or WebP (max 10MB)</small>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Unsplash Tab */}
          {activeTab === "unsplash" && (
            <div className="search-area">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search Unsplash..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch("unsplash")}
                />
                <button onClick={() => handleSearch("unsplash")} disabled={isLoading}>
                  <FiSearch size={18} />
                </button>
              </div>
              {suggestedKeywords.length > 0 && (
                <div className="keyword-suggestions">
                  <span className="suggestions-label">Suggestions:</span>
                  {suggestedKeywords.map((keyword, index) => (
                    <button
                      key={index}
                      className="keyword-chip"
                      onClick={() => {
                        setSearchQuery(keyword);
                        handleSearch("unsplash");
                      }}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              )}
              <div className="search-results-grid">
                {isLoading ? (
                  <div className="loading-state">
                    <FiLoader className="spin" size={32} />
                    <p>Searching...</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="empty-state">
                    <FiSearch size={48} />
                    <p>Search for free photos on Unsplash</p>
                  </div>
                ) : (
                  searchResults.map((image) => (
                    <div
                      key={image.id}
                      className={`search-result-item ${selectedImage?.id === image.id ? "selected" : ""}`}
                      onClick={() => handleSelectExternalImage(image)}
                    >
                      <img src={image.thumb} alt={image.alt} />
                      {selectedImage?.id === image.id && (
                        <div className="selected-badge">
                          <FiCheck size={20} />
                        </div>
                      )}
                      <div className="photo-credit">
                        by {image.photographer}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* Pexels Tab */}
          {activeTab === "pexels" && (
            <div className="search-area">
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search Pexels..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch("pexels")}
                />
                <button onClick={() => handleSearch("pexels")} disabled={isLoading}>
                  <FiSearch size={18} />
                </button>
              </div>
              {suggestedKeywords.length > 0 && (
                <div className="keyword-suggestions">
                  <span className="suggestions-label">Suggestions:</span>
                  {suggestedKeywords.map((keyword, index) => (
                    <button
                      key={index}
                      className="keyword-chip"
                      onClick={() => {
                        setSearchQuery(keyword);
                        handleSearch("pexels");
                      }}
                    >
                      {keyword}
                    </button>
                  ))}
                </div>
              )}
              <div className="search-results-grid">
                {isLoading ? (
                  <div className="loading-state">
                    <FiLoader className="spin" size={32} />
                    <p>Searching...</p>
                  </div>
                ) : searchResults.length === 0 ? (
                  <div className="empty-state">
                    <FiSearch size={48} />
                    <p>Search for free photos on Pexels</p>
                  </div>
                ) : (
                  searchResults.map((image) => (
                    <div
                      key={image.id}
                      className={`search-result-item ${selectedImage?.id === image.id ? "selected" : ""}`}
                      onClick={() => handleSelectExternalImage(image)}
                    >
                      <img src={image.thumb} alt={image.alt} />
                      {selectedImage?.id === image.id && (
                        <div className="selected-badge">
                          <FiCheck size={20} />
                        </div>
                      )}
                      <div className="photo-credit">
                        by {image.photographer}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}

          {/* AI Generate Tab */}
          {activeTab === "ai" && (
            <div className="ai-generate-area">
              <div className="ai-prompt-section">
                <textarea
                  className="ai-prompt-input"
                  placeholder="Describe the image you want to generate..."
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  rows={6}
                  disabled={aiGenerating}
                />
                <div className="ai-options-row">
                  <button
                    className="ai-generate-btn"
                    onClick={handleAIGenerate}
                    disabled={aiGenerating || !aiPrompt.trim()}
                  >
                    {aiGenerating ? (
                      <><FiLoader className="spin" size={16} /> Generating...</>
                    ) : (
                      <><FiZap size={16} /> Generate</>
                    )}
                  </button>
                </div>
              </div>

              {aiGenerating && (
                <div className="ai-generating-state">
                  <FiLoader className="spin" size={40} />
                  <p>Generating your image...</p>
                  <small>This may take 10-30 seconds</small>
                </div>
              )}

              {aiGeneratedImage && !aiGenerating && (
                <div className="ai-result">
                  <div className="ai-result-image">
                    <img src={aiGeneratedImage.url} alt="AI Generated" />
                    <div className="selected-badge">
                      <FiCheck size={20} />
                    </div>
                  </div>
                  <p className="ai-result-hint">Image saved to your gallery. Click "Use This Image" to select it.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="image-modal-footer">
          {currentImage && (
            <div className="current-image-preview">
              <span>Current:</span>
              <img src={currentImage} alt="Current" />
            </div>
          )}
          <div className="footer-actions">
            <button className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button
              className="btn-confirm"
              onClick={handleConfirmSelection}
              disabled={!selectedImage || savingExternal}
            >
              {savingExternal ? (
                <>
                  <FiLoader className="spin" /> Saving...
                </>
              ) : (
                <>
                  <FiCheck /> Use This Image
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      <style jsx="true">{`
        .image-modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          backdrop-filter: blur(4px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          padding: 20px;
        }

        .image-modal {
          background: var(--bland-white, #fff);
          border-radius: var(--bland-radius-lg, 16px);
          width: 100%;
          max-width: 900px;
          max-height: 85vh;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 24px 80px rgba(0, 0, 0, 0.18), 0 0 0 1px rgba(0, 0, 0, 0.05);
          font-family: 'DM Sans', -apple-system, sans-serif;
        }

        .image-modal-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 18px 24px;
          border-bottom: 1px solid var(--bland-gray-100, #F3F4F6);
        }

        .image-modal-header h3 {
          margin: 0;
          color: var(--bland-gray-900, #111827);
          font-family: 'Plus Jakarta Sans', sans-serif;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: -0.3px;
        }

        .modal-close-btn {
          background: transparent;
          border: none;
          color: var(--bland-gray-400, #9CA3AF);
          cursor: pointer;
          padding: 6px;
          border-radius: var(--bland-radius-sm, 8px);
          transition: all 0.2s;
        }

        .modal-close-btn:hover {
          background: var(--bland-gray-100, #F3F4F6);
          color: var(--bland-gray-700, #374151);
        }

        .image-modal-tabs {
          display: flex;
          gap: 4px;
          padding: 12px 24px;
          border-bottom: 1px solid var(--bland-gray-100, #F3F4F6);
          background: var(--bland-gray-50, #F9FAFB);
        }

        .tab-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 9px 16px;
          background: transparent;
          border: none;
          color: var(--bland-gray-500, #6B7280);
          font-size: 13px;
          font-weight: 500;
          font-family: 'DM Sans', sans-serif;
          border-radius: var(--bland-radius-sm, 8px);
          cursor: pointer;
          transition: all 0.2s;
        }

        .tab-btn:hover {
          background: var(--bland-gray-100, #F3F4F6);
          color: var(--bland-gray-800, #1F2937);
        }

        .tab-btn.active {
          background: var(--bland-purple-600, #7C3AED);
          color: #fff;
          box-shadow: 0 2px 8px rgba(124, 58, 237, 0.25);
        }

        .image-modal-content {
          flex: 1;
          overflow-y: auto;
          padding: 24px;
          min-height: 400px;
          background: var(--bland-white, #fff);
        }

        .gallery-grid,
        .search-results-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: 12px;
        }

        .gallery-item,
        .search-result-item {
          position: relative;
          aspect-ratio: 1;
          border-radius: var(--bland-radius-sm, 8px);
          overflow: hidden;
          cursor: pointer;
          border: 3px solid transparent;
          transition: all 0.2s;
        }

        .gallery-item:hover,
        .search-result-item:hover {
          border-color: var(--bland-purple-400, #A78BFA);
          transform: scale(1.02);
        }

        .gallery-item.selected,
        .search-result-item.selected {
          border-color: var(--bland-purple-600, #7C3AED);
        }

        .gallery-item img,
        .search-result-item img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .selected-badge {
          position: absolute;
          top: 8px;
          right: 8px;
          background: var(--bland-purple-600, #7C3AED);
          color: #fff;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .delete-btn {
          position: absolute;
          bottom: 8px;
          right: 8px;
          background: rgba(239, 68, 68, 0.9);
          color: #fff;
          border: none;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          opacity: 0;
          transition: opacity 0.2s;
        }

        .gallery-item:hover .delete-btn {
          opacity: 1;
        }

        .photo-credit {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 6px 8px;
          background: linear-gradient(transparent, rgba(0,0,0,0.7));
          color: #fff;
          font-size: 10px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .saving-overlay {
          position: absolute;
          inset: 0;
          background: rgba(124, 58, 237, 0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #fff;
        }

        .upload-area {
          display: flex;
          align-items: center;
          justify-content: center;
          min-height: 300px;
        }

        .upload-dropzone {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          border: 2px dashed var(--bland-gray-200, #E5E7EB);
          border-radius: var(--bland-radius-md, 12px);
          cursor: pointer;
          transition: all 0.2s;
          color: var(--bland-gray-400, #9CA3AF);
          text-align: center;
        }

        .upload-dropzone:hover {
          border-color: var(--bland-purple-400, #A78BFA);
          background: var(--bland-purple-50, #F5F3FF);
        }

        .upload-dropzone p {
          margin: 16px 0 8px;
          font-size: 16px;
          color: var(--bland-gray-800, #1F2937);
          font-weight: 600;
        }

        .upload-dropzone small {
          color: var(--bland-gray-400, #9CA3AF);
        }

        .search-area {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .search-bar {
          display: flex;
          gap: 8px;
        }

        .search-bar input {
          flex: 1;
          padding: 11px 16px;
          background: var(--bland-white, #fff);
          border: 1px solid var(--bland-gray-200, #E5E7EB);
          border-radius: var(--bland-radius-sm, 8px);
          color: var(--bland-gray-900, #111827);
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          outline: none;
          transition: border-color 0.2s;
        }

        .search-bar input:focus {
          border-color: var(--bland-purple-400, #A78BFA);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
        }

        .search-bar button {
          padding: 11px 20px;
          background: var(--bland-purple-600, #7C3AED);
          border: none;
          border-radius: var(--bland-radius-sm, 8px);
          color: #fff;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .search-bar button:hover {
          background: var(--bland-purple-700, #6D28D9);
        }

        .search-bar button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .keyword-suggestions {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          padding: 8px 0;
        }

        .suggestions-label {
          font-size: 12px;
          color: var(--bland-gray-400, #9CA3AF);
          margin-right: 4px;
        }

        .keyword-chip {
          padding: 5px 12px;
          background: var(--bland-gray-50, #F9FAFB);
          border: 1px solid var(--bland-gray-200, #E5E7EB);
          border-radius: 20px;
          color: var(--bland-gray-600, #4B5563);
          font-size: 12px;
          cursor: pointer;
          transition: all 0.2s;
        }

        .keyword-chip:hover {
          background: var(--bland-purple-50, #F5F3FF);
          border-color: var(--bland-purple-300, #C4B5FD);
          color: var(--bland-purple-700, #6D28D9);
        }

        .loading-state,
        .empty-state {
          grid-column: 1 / -1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px;
          color: var(--bland-gray-400, #9CA3AF);
          text-align: center;
        }

        .loading-state p,
        .empty-state p {
          margin-top: 16px;
        }

        .btn-upload {
          margin-top: 16px;
          padding: 10px 20px;
          background: var(--bland-purple-600, #7C3AED);
          border: none;
          border-radius: var(--bland-radius-sm, 8px);
          color: #fff;
          font-weight: 600;
          cursor: pointer;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .image-modal-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 24px;
          border-top: 1px solid var(--bland-gray-100, #F3F4F6);
          background: var(--bland-gray-50, #F9FAFB);
        }

        .current-image-preview {
          display: flex;
          align-items: center;
          gap: 10px;
          color: var(--bland-gray-500, #6B7280);
          font-size: 13px;
        }

        .current-image-preview img {
          width: 40px;
          height: 40px;
          object-fit: cover;
          border-radius: 6px;
          border: 1px solid var(--bland-gray-200, #E5E7EB);
        }

        .footer-actions {
          display: flex;
          gap: 10px;
        }

        .btn-cancel {
          padding: 10px 20px;
          background: transparent;
          border: 1px solid var(--bland-gray-200, #E5E7EB);
          border-radius: var(--bland-radius-sm, 8px);
          color: var(--bland-gray-600, #4B5563);
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-cancel:hover {
          background: var(--bland-gray-100, #F3F4F6);
          color: var(--bland-gray-800, #1F2937);
        }

        .btn-confirm {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 10px 24px;
          background: var(--bland-purple-600, #7C3AED);
          border: none;
          border-radius: var(--bland-radius-sm, 8px);
          color: #fff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-confirm:hover {
          background: var(--bland-purple-700, #6D28D9);
          box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
        }

        .btn-confirm:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        /* AI Generate Tab */
        .ai-generate-area {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .ai-prompt-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .ai-prompt-input {
          width: 100%;
          padding: 14px 16px;
          background: var(--bland-white, #fff);
          border: 1.5px solid var(--bland-gray-200, #E5E7EB);
          border-radius: var(--bland-radius-md, 12px);
          color: var(--bland-gray-900, #111827);
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          resize: vertical;
          outline: none;
          line-height: 1.6;
          min-height: 140px;
          box-sizing: border-box;
          transition: border-color 0.2s;
        }

        .ai-prompt-input:focus {
          border-color: var(--bland-purple-400, #A78BFA);
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08);
        }

        .ai-prompt-input::placeholder {
          color: var(--bland-gray-400, #9CA3AF);
        }

        .ai-prompt-input:disabled {
          opacity: 0.6;
          background: var(--bland-gray-50, #F9FAFB);
        }

        .ai-options-row {
          display: flex;
          align-items: flex-end;
          gap: 12px;
        }

        .ai-generate-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 11px 28px;
          background: linear-gradient(135deg, var(--bland-purple-600, #7C3AED), var(--bland-purple-500, #8B5CF6));
          border: none;
          border-radius: var(--bland-radius-sm, 8px);
          color: #fff;
          font-size: 14px;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          white-space: nowrap;
          margin-left: auto;
          box-shadow: 0 2px 10px rgba(124, 58, 237, 0.2);
        }

        .ai-generate-btn:hover:not(:disabled) {
          box-shadow: 0 6px 24px rgba(124, 58, 237, 0.35);
          transform: translateY(-2px);
        }

        .ai-generate-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .ai-generating-state {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 20px;
          color: var(--bland-purple-400, #A78BFA);
          text-align: center;
        }

        .ai-generating-state p {
          margin-top: 16px;
          font-size: 16px;
          font-weight: 600;
          color: var(--bland-gray-800, #1F2937);
        }

        .ai-generating-state small {
          margin-top: 6px;
          color: var(--bland-gray-400, #9CA3AF);
          font-size: 13px;
        }

        .ai-result {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 12px;
        }

        .ai-result-image {
          position: relative;
          max-width: 400px;
          border-radius: var(--bland-radius-md, 12px);
          overflow: hidden;
          border: 3px solid var(--bland-purple-600, #7C3AED);
          box-shadow: 0 8px 30px rgba(124, 58, 237, 0.15);
        }

        .ai-result-image img {
          width: 100%;
          display: block;
        }

        .ai-result-hint {
          font-size: 13px;
          color: var(--bland-gray-400, #9CA3AF);
        }
      `}</style>
    </div>
  );
};

export default ImageUploadModal;
